import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Badge, Card, Button, Modal, Form } from 'react-bootstrap';
import { getMyAssignmentsAction, updateAssignmentStatusAction } from '../../store/reducers/personalTask';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import { TASK_STATUS_CONST } from '../../store/constants';
import { toast } from 'react-toastify';
import { userManager } from '@app/utils/oidc';

const getStatusStyle = (color) => {
    const map = {
        'secondary': { bg: '#f6f8fa', text: '#57606a', border: '#d0d7de' },
        'info': { bg: '#ddf4ff', text: '#0969da', border: '#54aeff' },
        'warning': { bg: '#fff8c5', text: '#9a6700', border: '#d4a72c' },
        'success': { bg: '#dafbe1', text: '#1a7f37', border: '#2da44e' },
        'danger': { bg: '#ffebe9', text: '#cf222e', border: '#ff8182' }
    };
    return map[color] || map['secondary'];
};

const toDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return '';
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    return `${d}/${m}/${y}`;
};

const toDisplayDateTime = (dateStr) => {
    if (!dateStr) return <span className="text-muted fst-italic">N/A</span>;
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return <span className="text-muted fst-italic">N/A</span>;
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    const h = String(dt.getHours()).padStart(2, '0');
    const min = String(dt.getMinutes()).padStart(2, '0');
    const s = String(dt.getSeconds()).padStart(2, '0');
    return (
        <div className="d-flex flex-column align-items-center">
            <span style={{ fontWeight: 600 }}>{`${d}/${m}/${y}`}</span>
            <small className="text-muted" style={{ fontSize: '10px', marginTop: '-2px' }}>{`${h}:${min}:${s}`}</small>
        </div>
    );
};

const getDeadlineWarning = (assignment) => {
    if (assignment.status === 3 || !assignment.endDate) return null;
    const now = new Date();
    const end = new Date(assignment.endDate);
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { type: 'overdue', label: 'Quá hạn', color: '#721c24', bg: '#f8d7da', border: '#f5c6cb' };
    if (diffDays <= 3) return { type: 'soon', label: `Còn ${diffDays} ngày`, color: '#856404', bg: '#fff3cd', border: '#ffeeba' };
    return null;
};

const COLUMN_STYLE_MAP = {
    0: { headerBg: '#e1e5f2', headerText: '#333333', border: '#c4ccdd', bodyBg: '#f7f9fc' },
    1: { headerBg: '#d3e5ff', headerText: '#245b8f', border: '#aac6ff', bodyBg: '#f3f7ff' },
    3: { headerBg: '#cef4e4', headerText: '#247a55', border: '#a0e0c6', bodyBg: '#f1fbf6' },
    4: { headerBg: '#f8d7da', headerText: '#721c24', border: '#f5c6cb', bodyBg: '#fff5f5' },
};

const PersonalKanban = () => {
    const dispatch = useDispatch();
    const { myAssignments } = useSelector((state) => state.personalTask);
    const { listAccount } = useSelector((state) => state.account);

    const [filterFrom, setFilterFrom] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [filterTo, setFilterTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [draggingId, setDraggingId] = useState(null);
    const [detailItem, setDetailItem] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        dispatch(getMyAssignmentsAction({
            from: filterFrom || undefined,
            to: filterTo || undefined,
        }));
        if (!listAccount || listAccount.length === 0) {
            dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        }
        
        userManager.getUser().then(user => setCurrentUser(user));
    }, [dispatch]);

    const handleFilter = () => {
        dispatch(getMyAssignmentsAction({
            from: filterFrom || undefined,
            to: filterTo || undefined,
        }));
    };

    const getDisplayName = (userId) => {
        if (!userId) return 'Không rõ';
        // Tìm theo accountId (ID của Account) hoặc username
        const accInfo = listAccount?.find(a => 
            String(a.accountId) === String(userId) || 
            String(a.account?.username) === String(userId)
        );
        
        if (!accInfo) return userId; 
        
        const fullName = `${accInfo.lastName || ''} ${accInfo.middleName || ''} ${accInfo.firstName || ''}`.replace(/\s+/g, ' ').trim();
        const username = accInfo.account?.username;
        
        if (fullName && username) return `${fullName} (${username})`;
        return fullName || username || userId;
    };

    const handleMoveTask = async (projectId, newStatus) => {
        try {
            await dispatch(updateAssignmentStatusAction({ projectId, data: { status: newStatus } })).unwrap();
            dispatch(getMyAssignmentsAction({
                from: filterFrom || undefined,
                to: filterTo || undefined,
            }));
            
            // Cập nhật detailItem nếu đang mở để UI đồng bộ ngay
            if (detailItem && detailItem.projectId === projectId) {
                setDetailItem(prev => ({
                    ...prev,
                    status: newStatus,
                    members: prev.members.map(m => {
                        // Cập nhật record của chính mình trong danh sách member
                        // Ở đây m.userId có thể là string hoặc Guid object tùy API
                        const isMe = String(m.userId || m.UserId) === String(prev.userId || prev.UserId);
                        if (isMe) {
                            return { ...m, status: newStatus, Status: newStatus, actualEndDate: newStatus === 3 ? new Date().toISOString() : null };
                        }
                        return m;
                    })
                }));
            }

            toast.success('Cập nhật trạng thái thành công');
        } catch {
            toast.error('Cập nhật thất bại');
        }
    };

    const handleDragStart = (projectId) => setDraggingId(projectId);
    const handleDragEnd = () => setDraggingId(null);
    const handleDropOnColumn = async (newStatus) => {
        if (draggingId !== null) {
            const item = normalizedAssignments.find(a => a.projectId === draggingId);
            if (item && item.status !== newStatus) {
                await handleMoveTask(draggingId, newStatus);
            }
            setDraggingId(null);
        }
    };

    const normalizedAssignments = useMemo(() => {
        return (myAssignments || []).map(a => {
            // API trả về Members (PascalCase) - thử tất cả biến thể có thể
            let rawMembers = a.Members ?? a.members ?? a.ProjectMembers ?? a.projectMembers ?? [];
            if (rawMembers && rawMembers.$values) {
                rawMembers = rawMembers.$values;
            }
            const members = Array.isArray(rawMembers) ? rawMembers : [];

            return {
                ...a,
                projectId: a.ProjectId ?? a.projectId,
                projectName: a.ProjectName ?? a.projectName,
                projectDescription: a.ProjectDescription ?? a.projectDescription,
                startDate: a.StartDate ?? a.startDate,
                endDate: a.EndDate ?? a.endDate,
                createdBy: a.CreatedBy ?? a.createdBy,
                userId: a.UserId ?? a.userId,
                status: a.Status ?? a.status ?? 0,
                projectStatus: a.ProjectStatus ?? a.projectStatus ?? 0,
                projectActualEndDate: a.ActualEndDate ?? a.actualEndDate ?? a.projectActualEndDate,
                members: members
            };
        });
    }, [myAssignments]);

    const assignmentsByStatus = useMemo(() => {
        const result = {};
        TASK_STATUS_CONST.forEach(col => {
            result[col.value] = normalizedAssignments.filter(a => Number(a.status) === Number(col.value));
        });
        return result;
    }, [normalizedAssignments]);

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <div>
                    <h2 className="mb-1 text-dark fw-bold">Nhiệm vụ cá nhân</h2>
                    <p className="text-muted mb-0" style={{ fontSize: '15px' }}>Các công việc được giao cho bạn</p>
                </div>
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    <Form.Control
                        type="date"
                        value={filterFrom}
                        onChange={(e) => setFilterFrom(e.target.value)}
                        style={{ width: 180, fontSize: '15px' }}
                        title="Từ ngày"
                    />
                    <span className="text-muted fw-bold">–</span>
                    <Form.Control
                        type="date"
                        value={filterTo}
                        onChange={(e) => setFilterTo(e.target.value)}
                        style={{ width: 180, fontSize: '15px' }}
                        title="Đến ngày"
                    />
                    <Button variant="primary" onClick={handleFilter} style={{ padding: '6px 20px', fontSize: '15px' }}>Lọc</Button>
                    <Button variant="outline-secondary" onClick={() => {
                        setFilterFrom(''); setFilterTo('');
                        dispatch(getMyAssignmentsAction({}));
                    }} style={{ padding: '6px 15px', fontSize: '15px' }}>Xoá lọc</Button>
                </div>
            </div>

            <Row>
                {TASK_STATUS_CONST.map(col => {
                    const list = assignmentsByStatus[col.value] || [];
                    const styleCfg = COLUMN_STYLE_MAP[col.value] || COLUMN_STYLE_MAP[0];

                    return (
                        <Col key={col.value} md={3}>
                            <div
                                className="card mb-3 shadow-sm"
                                style={{ borderColor: styleCfg.border, borderWidth: 1, borderStyle: 'solid', backgroundColor: '#fff' }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleDropOnColumn(col.value)}
                            >
                                <div
                                    className="card-header d-flex align-items-center"
                                    style={{ backgroundColor: styleCfg.headerBg, borderBottomColor: styleCfg.border, borderBottomStyle: 'solid', borderBottomWidth: 1, color: styleCfg.headerText, fontWeight: 700, fontSize: 17, height: 60 }}
                                >
                                    <span>{col.label}</span>
                                    <div style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>{list.length}</div>
                                    <div style={{ width: 28 }}></div>
                                </div>

                                <div
                                    className="card-body p-2"
                                    style={{ minHeight: 400, backgroundColor: styleCfg.bodyBg }}
                                >
                                    {list.map(assignment => {
                                        const warning = getDeadlineWarning(assignment);
                                        const isBeingDragged = draggingId === assignment.projectId;
                                        
                                        const isAdmin = currentUser?.profile?.role === 'admin' || 
                                                       (Array.isArray(currentUser?.profile?.role) && currentUser.profile.role.includes('admin'));
                                        
                                        const isMyTask = String(currentUser?.profile?.sub) === String(assignment.userId);

                                        return (
                                            <Card
                                                key={assignment.projectId}
                                                className="mb-2 shadow-sm"
                                                draggable
                                                onDragStart={() => handleDragStart(assignment.projectId)}
                                                onDragEnd={handleDragEnd}
                                                style={{
                                                    opacity: isBeingDragged ? 0.5 : 1,
                                                    cursor: 'grab',
                                                    borderLeft: warning ? `4px solid ${warning.color}` : undefined,
                                                }}
                                            >
                                                <Card.Body className="p-3">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h5
                                                            className="mb-0"
                                                            style={{ fontWeight: 700, fontSize: 16, flex: 1, color: '#0056b3', cursor: 'pointer', textDecoration: 'none' }}
                                                            onClick={() => setDetailItem(assignment)}
                                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                        >
                                                            {assignment.projectName}
                                                        </h5>
                                                        {warning && (
                                                            <span style={{
                                                                marginLeft: 8,
                                                                padding: '3px 10px',
                                                                borderRadius: 999,
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                background: warning.bg,
                                                                color: warning.color,
                                                                border: `1px solid ${warning.border}`,
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {warning.label}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {assignment.projectDescription && (
                                                        <p className="text-muted mb-3" style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                                            {assignment.projectDescription}
                                                        </p>
                                                    )}

                                                    <div style={{ fontSize: 14, color: '#444', lineHeight: '1.6' }} className="mb-3">
                                                        {assignment.startDate && <div>📅 <strong>Bắt đầu:</strong> {toDisplayDate(assignment.startDate)}</div>}
                                                        {assignment.endDate && <div>⏰ <strong>Hạn chót:</strong> {toDisplayDate(assignment.endDate)}</div>}
                                                        <div>👤 <strong>Giao bởi:</strong> {getDisplayName(assignment.createdBy)}</div>
                                                        {isAdmin && assignment.userId && (
                                                            <div>👷 <strong>Người thực hiện:</strong> {getDisplayName(assignment.userId)}</div>
                                                        )}
                                                        
                                                        <div className="mt-2 pt-2 border-top">
                                                            <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: 12 }}>
                                                                <span className="text-muted">Tiến độ nhóm:</span>
                                                                <span className="fw-bold text-success">{assignment.progress || 0}%</span>
                                                            </div>
                                                            <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                                                                <div 
                                                                    className="progress-bar bg-success" 
                                                                    role="progressbar" 
                                                                    style={{ width: `${assignment.progress || 0}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>

                                                        {assignment.projectStatus === 1 && (
                                                            <div className="mt-3 p-2 rounded-2 bg-success bg-opacity-10 border border-success border-opacity-25">
                                                                <div className="d-flex align-items-center text-success mb-1" style={{ fontSize: '11px', fontWeight: 800 }}>
                                                                    <span className="me-1">🏁</span> DỰ ÁN HOÀN THÀNH
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center px-1">
                                                                    <span className="text-muted" style={{ fontSize: '11px' }}>Thời gian:</span>
                                                                    {toDisplayDateTime(assignment.projectActualEndDate)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-2">
                                                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                                            {isMyTask && assignment.projectStatus !== 1 && TASK_STATUS_CONST.filter(c => c.value !== assignment.status).map(c => (
                                                                <Button
                                                                    key={c.value}
                                                                    variant={`outline-${c.color}`}
                                                                    style={{ fontSize: 12, padding: '4px 10px', fontWeight: 600 }}
                                                                    onClick={() => handleMoveTask(assignment.projectId, c.value)}
                                                                >
                                                                    → {c.label}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                        <Button
                                                            variant="outline-secondary"
                                                            style={{ fontSize: 12, padding: '4px 12px', fontWeight: 600 }}
                                                            onClick={() => setDetailItem(assignment)}
                                                        >
                                                            Chi tiết
                                                        </Button>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {/* Modal chi tiết công việc */}
            <Modal
                show={!!detailItem}
                onHide={() => setDetailItem(null)}
                size="lg"
                centered
                backdropClassName="custom-modal-backdrop"
                style={{ zIndex: 1060 }}
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>{detailItem?.projectName}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: '#fff' }}>
                    {detailItem && (
                        <>
                            <p className="mb-4" style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: '1.6', color: '#333' }}>
                                {detailItem.projectDescription || 'Không có mô tả'}
                            </p>

                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="p-3 rounded bg-light h-100" style={{ fontSize: 14 }}>
                                        <div className="mb-2"><strong>📅 Ngày bắt đầu:</strong> {toDisplayDate(detailItem.startDate) || 'Chưa có'}</div>
                                        <div className="mb-2"><strong>⏰ Hạn chót:</strong> {toDisplayDate(detailItem.endDate) || 'Chưa có'}</div>
                                        <div className="mb-2"><strong>👤 Người giao:</strong> {getDisplayName(detailItem.createdBy)}</div>
                                        <div className="mb-2">
                                            <strong>🎯 Dự án:</strong> {detailItem.projectStatus === 1 
                                                ? <span className="text-success fw-bold">Hoàn thành lúc {toDisplayDateTime(detailItem.projectActualEndDate)}</span>
                                                : <span className="text-danger fw-bold">Chưa hoàn thành</span>
                                            }
                                        </div>
                                        <div className="mt-3 pt-2 border-top">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <strong>📊 Tiến độ chung của nhóm:</strong>
                                                <span className="fw-bold text-success">{detailItem.progress || 0}%</span>
                                            </div>
                                            <div className="progress" style={{ height: 10, borderRadius: 5 }}>
                                                <div 
                                                    className="progress-bar bg-success progress-bar-striped progress-bar-animated" 
                                                    role="progressbar" 
                                                    style={{ width: `${detailItem.progress || 0}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 rounded border h-100" style={{ fontSize: 14 }}>
                                        <div className="mb-2 text-muted">Trạng thái của bạn:</div>
                                        {(() => {
                                            const currentStatus = TASK_STATUS_CONST.find(s => Number(s.value) === Number(detailItem.status));
                                            const style = getStatusStyle(currentStatus?.color || 'secondary');
                                            return (
                                                <span 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontWeight: '600',
                                                        backgroundColor: style.bg,
                                                        color: style.text,
                                                        border: `1px solid ${style.border}`,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        textTransform: 'uppercase',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                    {currentStatus?.label || 'Cần làm'}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {detailItem.members && detailItem.members.length > 0 && (
                                <>
                                    <h6 className="fw-bold mb-3 border-bottom pb-2">👥 Những người cùng tham gia ({detailItem.members.length})</h6>
                                    <div className="table-responsive border rounded" style={{ maxHeight: 250, overflowY: 'auto' }}>
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th className="ps-3 py-2">Thành viên</th>
                                                    <th className="text-center py-2">Trạng thái</th>
                                                    <th className="text-center py-2">Hoàn thành lúc</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detailItem.members.map((m, i) => {
                                                    const mStatusValue = m.status ?? m.Status ?? 0;
                                                    const mStatus = TASK_STATUS_CONST.find(s => Number(s.value) === Number(mStatusValue));
                                                    return (
                                                        <tr key={i}>
                                                            <td className="ps-3 py-2 align-middle fw-500">{getDisplayName(m.userId || m.UserId)}</td>
                                                            <td className="text-center py-2 align-middle">
                                                                {(() => {
                                                                    const style = getStatusStyle(mStatus?.color || 'secondary');
                                                                    return (
                                                                        <span 
                                                                            style={{ 
                                                                                fontSize: '0.65rem', 
                                                                                padding: '2px 8px',
                                                                                borderRadius: '10px',
                                                                                fontWeight: '600',
                                                                                backgroundColor: style.bg,
                                                                                color: style.text,
                                                                                border: `1px solid ${style.border}`,
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                gap: '4px',
                                                                                textTransform: 'uppercase',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            <span style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                                            {mStatus?.label || 'Cần làm'}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="text-center py-2 align-middle text-muted">
                                                                {m.actualEndDate ? toDisplayDateTime(m.actualEndDate) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" size="sm" onClick={() => setDetailItem(null)}>Đóng</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PersonalKanban;
